import { useState, useEffect } from 'react';
import { X, Film, Tv, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const GENRE_OPTIONS = [
  'Action', 'Adventure', 'Comedy', 'Drama', 'Fantasy', 'Horror',
  'Mystery', 'Romance', 'Sci-Fi', 'Thriller', 'Documentary', 'Animation'
];

const MOOD_OPTIONS = [
  { value: 'lighthearted', label: 'Lighthearted & Fun' },
  { value: 'feel-good', label: 'Feel-good & Uplifting' },
  { value: 'thought-provoking', label: 'Thought-provoking' },
  { value: 'suspenseful', label: 'Suspenseful & Tense' },
  { value: 'funny', label: 'Funny & Humorous' },
  { value: 'inspiring', label: 'Inspiring & Motivational' },
  { value: 'relaxing', label: 'Relaxing & Calm' },
  { value: 'intense', label: 'Intense & Gripping' }
];

const DEALBREAKER_OPTIONS = [
  { value: 'dog_dies', label: 'Dog/pet dies', type: 'content' },
  { value: 'emotionally_heavy', label: 'Super emotionally heavy', type: 'intensity' },
  { value: 'children_in_distress', label: 'Children in distress', type: 'content' },
  { value: 'occult_themes', label: 'Significant devil/satanic/occult storyline', type: 'content' },
  { value: 'grotesque_violence', label: 'Grotesque violence', type: 'content' },
  { value: 'raunchy_content', label: 'Raunchy or excessive bathroom humor', type: 'content' },
  { value: 'too_depressing', label: 'Too intense or depressing', type: 'intensity' },
  { value: 'jump_scares', label: 'Jump scares / horror elements', type: 'content' },
  { value: 'cringe_humor', label: 'Cringe/awkward humor', type: 'content' },
  { value: 'animal_harm', label: 'Animal harm or abuse', type: 'content' }
];

export default function WatchPreferencesModal({ userId, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [loadingPrefs, setLoadingPrefs] = useState(true);

  // Preferences state
  const [genrePreferences, setGenrePreferences] = useState([]);
  const [contentTypePreference, setContentTypePreference] = useState('both');
  const [minReleaseYear, setMinReleaseYear] = useState(null);
  const [attentionLevel, setAttentionLevel] = useState(5);
  const [moodPreferences, setMoodPreferences] = useState([]);
  const [dealbreakers, setDealbreakers] = useState([]);
  const [maxIntensityLevel, setMaxIntensityLevel] = useState(7);
  const [familyViewingOnly, setFamilyViewingOnly] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, [userId]);

  const loadPreferences = async () => {
    try {
      const response = await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get-preferences',
          userId
        })
      });

      const data = await response.json();

      if (data.success && data.preferences) {
        const prefs = data.preferences;
        setGenrePreferences(prefs.genre_preferences || []);
        setContentTypePreference(prefs.content_type_preference || 'both');
        setMinReleaseYear(prefs.min_release_year || null);
        setAttentionLevel(prefs.attention_level || 5);
        setMoodPreferences(prefs.mood_preferences || []);
        setDealbreakers(prefs.dealbreakers || []);
        setMaxIntensityLevel(prefs.max_intensity_level || 7);
        setFamilyViewingOnly(prefs.family_viewing_only || false);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error('Failed to load preferences');
    } finally {
      setLoadingPrefs(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const response = await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save-preferences',
          userId,
          genrePreferences,
          contentTypePreference,
          minReleaseYear,
          attentionLevel,
          moodPreferences,
          dealbreakers,
          maxIntensityLevel,
          avoidTags: dealbreakers.map(d => d.value),
          familyViewingOnly
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Preferences saved!');
        onSave?.(data.preferences);
        onClose();
      } else {
        throw new Error(data.error || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error(error.message || 'Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const toggleGenre = (genre) => {
    setGenrePreferences(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const toggleMood = (mood) => {
    setMoodPreferences(prev =>
      prev.includes(mood)
        ? prev.filter(m => m !== mood)
        : [...prev, mood]
    );
  };

  const toggleDealbreaker = (dealbreaker) => {
    setDealbreakers(prev => {
      const exists = prev.find(d => d.value === dealbreaker.value);
      if (exists) {
        return prev.filter(d => d.value !== dealbreaker.value);
      } else {
        return [...prev, dealbreaker];
      }
    });
  };

  if (loadingPrefs) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-8 max-w-2xl w-full">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-violet-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Watch Preferences</h2>
              <p className="text-indigo-100 text-sm">Help AI recommend perfect shows & movies</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Content Type Preference */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              What do you prefer to watch?
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'both', label: 'Movies & TV', icon: Sparkles },
                { value: 'movie', label: 'Movies Only', icon: Film },
                { value: 'tv', label: 'TV Shows Only', icon: Tv }
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setContentTypePreference(value)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    contentTypePreference === value
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <Icon className={`w-6 h-6 mx-auto mb-2 ${
                    contentTypePreference === value ? 'text-indigo-600' : 'text-stone-400'
                  }`} />
                  <div className="text-sm font-medium text-stone-900">{label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Min Release Year */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Released in or after
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={minReleaseYear || ''}
                onChange={(e) => setMinReleaseYear(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Any year"
                min="1900"
                max={new Date().getFullYear()}
                className="flex-1 px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <button
                onClick={() => setMinReleaseYear(null)}
                className="px-4 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg font-medium transition-colors"
              >
                Clear
              </button>
            </div>
            <p className="text-xs text-stone-500 mt-2">
              For TV series, the latest season year is used
            </p>
          </div>

          {/* Genre Preferences */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Favorite Genres
            </label>
            <div className="flex flex-wrap gap-2">
              {GENRE_OPTIONS.map(genre => (
                <button
                  key={genre}
                  onClick={() => toggleGenre(genre)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    genrePreferences.includes(genre)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          {/* Attention Level */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              How much attention do you want to give? <span className="font-normal text-stone-600">({attentionLevel}/10)</span>
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={attentionLevel}
              onChange={(e) => setAttentionLevel(parseInt(e.target.value))}
              className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-stone-600 mt-2">
              <span>Background</span>
              <span>Casual</span>
              <span>Full Focus</span>
            </div>
          </div>

          {/* Mood Preferences */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Preferred Moods
            </label>
            <div className="grid grid-cols-2 gap-2">
              {MOOD_OPTIONS.map(mood => (
                <button
                  key={mood.value}
                  onClick={() => toggleMood(mood.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                    moodPreferences.includes(mood.value)
                      ? 'bg-violet-600 text-white'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  {mood.label}
                </button>
              ))}
            </div>
          </div>

          {/* Max Intensity Level */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Max Intensity Level <span className="font-normal text-stone-600">({maxIntensityLevel}/10)</span>
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={maxIntensityLevel}
              onChange={(e) => setMaxIntensityLevel(parseInt(e.target.value))}
              className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
            />
            <div className="flex justify-between text-xs text-stone-600 mt-2">
              <span>Very Light</span>
              <span>Moderate</span>
              <span>Very Intense</span>
            </div>
          </div>

          {/* Family Viewing Toggle */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={familyViewingOnly}
                onChange={(e) => setFamilyViewingOnly(e.target.checked)}
                className="mt-1 h-5 w-5 text-amber-600 focus:ring-amber-500 border-amber-300 rounded"
              />
              <div>
                <div className="font-semibold text-stone-900">Family Viewing Mode</div>
                <div className="text-sm text-stone-600">
                  Only recommend teen-safe content (no mature themes, language, or content)
                </div>
              </div>
            </label>
          </div>

          {/* Dealbreakers */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <label className="text-sm font-semibold text-stone-900">
                Dealbreakers - Never Recommend
              </label>
            </div>
            <div className="space-y-2">
              {DEALBREAKER_OPTIONS.map(dealbreaker => (
                <label
                  key={dealbreaker.value}
                  className="flex items-center gap-3 p-3 hover:bg-red-100 rounded-lg cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={dealbreakers.some(d => d.value === dealbreaker.value)}
                    onChange={() => toggleDealbreaker(dealbreaker)}
                    className="h-5 w-5 text-red-600 focus:ring-red-500 border-red-300 rounded"
                  />
                  <span className="text-sm text-stone-900">{dealbreaker.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-stone-300 text-stone-700 font-semibold rounded-xl hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-violet-700 transition-all disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
